import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InfoIcon from "@mui/icons-material/Info";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { ToastContainer } from "react-toastify";
import axiosInstance from "../../helpers/axios";
import formatDate, {
  errorAlert,
  customStyles,
  formatMonthDate,
  successAlert,
} from "../../utils/services";
import CustomTooltip from "../../components/ui/CustomTootltip";
import NoDataFound from "../../components/NoDataFound";
import TableSkeletonLoader from "../../components/ui/TableSkeletonLoader";
import CustomDateRangePicker from "../../components/ui/CustomDateRangePicker";
import { fetchAllMatters } from "../../store/slices/matterSlice";
import { fetchEmployees } from "../../store/slices/employeesSlice";

const TimeSpentBin = () => {
  const dispatch = useDispatch();
  const { filteredMatters } = useSelector((state) => state.matter);
  const { filteredEmployees } = useSelector((state) => state.employee);
  const [workHours, setWorkHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatter, setSelectedMatter] = useState("");
  const [selectedResource, setSelectedResource] = useState("");
  const [startSelectedDate, setStartSelectedDate] = useState(() => {
    const today = new Date();
    const pastDate = new Date(today.setDate(today.getDate() - 2));
    return formatDate(pastDate);
  });
  const [endSelectedDate, setEndSelectedDate] = useState(
    formatDate(new Date())
  );
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const today = new Date();
    const pastDate = new Date(today.setDate(today.getDate() - 2));
    return {
      start: formatDate(pastDate),
      end: formatDate(new Date()),
    };
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const matterOptions = useMemo(
    () => [
      { value: "", label: "All Matters" },
      ...filteredMatters.filter((option) => option.value),
    ],
    [filteredMatters]
  );
  const resourceOptions = useMemo(
    () => [
      { value: "", label: "All Resources" },
      ...filteredEmployees.filter((option) => option.value),
    ],
    [filteredEmployees]
  );

  useEffect(() => {
    dispatch(fetchAllMatters());
    dispatch(fetchEmployees());
  }, [dispatch]);

  const fetchBinnedWorkHours = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/workhour/bin", {
        params: {
          matterId: selectedMatter,
          user: selectedResource,
          fromDate: startSelectedDate,
          toDate: endSelectedDate,
          limit: rowsPerPage,
          page,
        },
      });
      setWorkHours(data.workHours || []);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      errorAlert("Unable to fetch Bin entries.");
    } finally {
      setLoading(false);
    }
  }, [
    endSelectedDate,
    page,
    rowsPerPage,
    selectedMatter,
    selectedResource,
    startSelectedDate,
  ]);

  useEffect(() => {
    fetchBinnedWorkHours();
  }, [fetchBinnedWorkHours]);

  const handleRestore = async (entryId) => {
    try {
      await axiosInstance.post(`/workhour/bin/restore/${entryId}`);
      await fetchBinnedWorkHours();
      successAlert("Entry restored to Time Spent.");
    } catch (error) {
      errorAlert("Unable to restore this entry.");
    }
  };

  const handleFinalDelete = async (entryId) => {
    if (!window.confirm("Archive the binned time? Any remaining time stays in TS and invoices.")) return;

    try {
      await axiosInstance.delete(`/workhour/bin/${entryId}`);
      await fetchBinnedWorkHours();
      successAlert("Entry marked for archive.");
    } catch (error) {
      errorAlert("Unable to delete this Bin entry.");
    }
  };

  const handleMatterChange = (option) => {
    setSelectedMatter(option?.value || "");
    setPage(0);
  };

  const handleResourceChange = (option) => {
    setSelectedResource(option?.value || "");
    setPage(0);
  };

  const handleDateRangeChange = (start, end) => {
    setSelectedDateRange({ start, end });
    setStartSelectedDate(start || "");
    setEndSelectedDate(end || "");
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <ToastContainer />
      <Paper elevation={0} className="rounded-full" sx={{ mb: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={3} display="flex" alignItems="center">
            <Typography variant="h5">Bin</Typography>
            <CustomTooltip text="Restore binned hours to include them in Time Spent and invoices again. Archiving partial bin time keeps the remaining time and the resource’s recorded hours intact.">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </CustomTooltip>
          </Grid>
          <Grid item xs={12} md={2}>
            <Select
              options={matterOptions}
              value={
                matterOptions.find((option) => option.value === selectedMatter) ||
                matterOptions[0]
              }
              onChange={handleMatterChange}
              isSearchable
              placeholder="All Matters"
              styles={customStyles}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Select
              options={resourceOptions}
              value={
                resourceOptions.find(
                  (option) => option.value === selectedResource
                ) || resourceOptions[0]
              }
              onChange={handleResourceChange}
              isSearchable
              placeholder="All Resources"
              styles={customStyles}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={3}
            display="flex"
            alignItems="center"
            gap={1}
          >
            <CustomTooltip text="Select Dates">
              <CustomDateRangePicker
                startDate={selectedDateRange.start}
                endDate={selectedDateRange.end}
                onDateRangeChange={handleDateRangeChange}
                iconSize={24}
              />
            </CustomTooltip>
            {startSelectedDate && endSelectedDate && (
              <Typography variant="body2">
                {formatMonthDate(startSelectedDate)} -{" "}
                {formatMonthDate(endSelectedDate)}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <TableContainer sx={{ maxHeight: "75vh" }}>
          <Table stickyHeader size="small" aria-label="binned time entries">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Matter</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Binned Hours</TableCell>
                <TableCell align="right">Remaining TS Hours</TableCell>
                <TableCell>Binned On</TableCell>
                <TableCell align="center">Roll Back</TableCell>
                <TableCell align="center">Del</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeletonLoader rows={6} colSpan={9} />
              ) : workHours.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <NoDataFound message="No binned time entries found." />
                  </TableCell>
                </TableRow>
              ) : (
                workHours.map((entry) => (
                  <TableRow key={entry._id}>
                    <TableCell>{formatMonthDate(entry.workDate)}</TableCell>
                    <TableCell>{entry.user?.fullName || "-"}</TableCell>
                    <TableCell>{entry.matter?.name || "-"}</TableCell>
                    <TableCell sx={{ minWidth: 260 }}>
                      {entry.workDescription}
                    </TableCell>
                    <TableCell align="right">
                      {Number(entry.workHour || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">{Number(entry.remainingWorkHour || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      {entry.binnedAt ? formatMonthDate(entry.binnedAt) : "-"}
                    </TableCell>
                    <TableCell align="center">
                      <CustomTooltip text="Restore to Time Spent">
                        <IconButton
                          color="primary"
                          onClick={() => handleRestore(entry._id)}
                        >
                          <SettingsBackupRestoreIcon />
                        </IconButton>
                      </CustomTooltip>
                    </TableCell>
                    <TableCell align="center">
                      <CustomTooltip text="Delete to archive">
                        <IconButton
                          color="error"
                          onClick={() => handleFinalDelete(entry._id)}
                        >
                          <DeleteForeverIcon />
                        </IconButton>
                      </CustomTooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default TimeSpentBin;
